import { scaleLinear } from 'd3-scale'
import * as d3 from 'd3-selection'
import * as array from 'd3-array'
import * as colors from 'colors'

const labelAttrs = {
  horizontalPadding: 30, // if we are using an ellipsis cutoff, the padding from the edge
  labelHeightOfStagePercent: 0.25,
  labelHeightAddition: 18,
  maxFont: 40,
  minFont: 10,
}

const crop = (selection) => {
  selection.each(function (d) {
    let width = d3.select(this.parentNode.parentNode).datum().bottomWidth
      , ellipsis = '...'
      , textLengthBuffer = 2 // To prevent floating point flickering in safari

    while (Math.floor(this.getComputedTextLength()) - textLengthBuffer > width - labelAttrs.horizontalPadding && this.textContent !== ellipsis) {
      d3.select(this).text(`${d3.select(this).text().slice(0, -4)}${ellipsis}`)
    }
  })
}

const getLabels = (stage, config) => {
  let labels = []
    , textVariation = colors.getTextVariation(colors.lookup(stage.color))

  if (stage.title) {
    labels.push({type: 'title', text: stage.title, textVariation})
  }

  if (config.value && stage.raw) {
    labels.push({type: 'value', text: stage.formatted, textVariation})
  }

  return labels
}

const getLongestVisibleLabel = (stage, config) => {
  return getLabels(stage, config)
    .map(label => String(label.text))
    .reduce((a, b) => a.length > b.length ? a : b, '')
}

const getLabelHeight = (stage) => {
  return ((stage.bottom - stage.top) * labelAttrs.labelHeightOfStagePercent) + labelAttrs.labelHeightAddition
}

export default (selection, config) => {
  // Determine the font size that should be used for the stage labels.

  // The font size will be the same for every stage!
  // If the label doesn't fit, we don't show it.

  // First, Find the ideal font size for each stage. The size is restricted
  // by the narrowest part of the drawing and the height available
  let data = selection.data()
    , showingValueLabel = !!data.find(stage => config.value && stage.raw)
    , showingTitleLabel = !!data.find(stage => stage.title)
    , labelStub = selection.append('g')
                           .attr('class', 'label-group')
                           .append('text')
                           .attr('class', 'title')

  data.forEach(stage => {
    let height = getLabelHeight(stage)

    if (showingValueLabel && showingTitleLabel) {
      // Showing two labels, so we have halve the height
      height /= 2
    }

    labelStub.text(getLongestVisibleLabel(stage, config))
             .attr('font-size', '1px') // Initialize so we can determine computed text length

    // Determine the font size that fits in the ideal width
    let fontWidth = Math.min(stage.bottomWidth - labelAttrs.horizontalPadding, (stage.bottomWidth - labelAttrs.horizontalPadding) / labelStub.node().getComputedTextLength())
      , fontSize = Math.min(height, fontWidth) // now also cap based on the individual label height available

    stage.fontSize = fontSize
  })

  // Remove the placeholder stub used to calculate font sizes
  labelStub.remove()

  // Store the font size so each stage can use it.
  let fontSize = Math.min(labelAttrs.maxFont, array.min(data, stage => stage.fontSize))

  // Now store the label font size on each stage. This is needed for percentages
  data.forEach(stage => {
    // If it's lower than our min font size, don't show it.
    stage.fontSize = fontSize < labelAttrs.minFont ? 0 : fontSize
  })

  selection.each(function (d) {
    let container = d3.select(this)
      , group = container.selectAll('g.label-group')
                         .data(d => [d])
                         .enter()
                         .append('g')
                           .attr('class', 'label-group')
                         .merge(container.selectAll('g.label-group'))
      , label = group.selectAll('g.label')
                     .data(d => {
                       return getLabels(d, config)
                     }, d => d.type)

    label.exit().remove()

    label = label.enter()
                 .append('g')
                 .merge(label)
                 .attr('class', d => `label label-on-colored-background ${d.type} ${d.textVariation}`)
                 .attr('transform', `translate(${d.dimensions.center}, ${d.top})`)

    let labelText = label.selectAll('text')
                         .data(d => [d])
    labelText.exit().remove()
    labelText = labelText.enter()
                         .append('text')
                         .merge(labelText)
    labelText.text(d => d.text)
             .style('font-size', d.fontSize)
             .call(crop)
             .attr('dy', d => {
               if (d.type == 'title') {
                 return showingValueLabel ? `-.5em` : null
               }
               if (d.type == 'value') {
                 return showingTitleLabel ? `.5em` : null
               }
             })
    group.attr('transform', d => `translate(0, ${(d.bottom - d.top) / 2})`)
  })
}
